import React from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import styles from './LegalDocument.module.scss';

// renders [text](url) markdown links, bare URLs and e-mail addresses
const renderLinks = (text) => {
  const re = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s)]+)|([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;
  const out = [];
  let last = 0;
  let m = re.exec(text);
  while (m !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[4]) {
      out.push(<a key={m.index} href={`mailto:${m[4]}`}>{m[4]}</a>);
    } else {
      const href = m[2] || m[3];
      const label = m[1] || m[3];
      out.push(href.startsWith('/')
        ? <Link key={m.index} to={href}>{label}</Link>
        : <a key={m.index} href={href} target="_blank" rel="noopener noreferrer">{label}</a>);
    }
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// renders **bold** segments, links inside them included
const renderInline = (text) => (text || '').split('**').map((part, i) => (
  i % 2 === 1
    ? <strong key={i}>{renderLinks(part)}</strong>
    : <React.Fragment key={i}>{renderLinks(part)}</React.Fragment>
));

const Item = ({ item }) => {
  if (item.table) {
    return (
      <div className={styles.tableWrap}>
        <table>
          {item.table.head && (
            <thead>
              <tr>{item.table.head.map((cell, i) => <th key={i}>{renderInline(cell)}</th>)}</tr>
            </thead>
          )}
          <tbody>
            {item.table.rows.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => <td key={j}>{renderInline(cell)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (item.list) {
    const ListTag = item.type ? 'ol' : 'ul';
    return (
      <ListTag type={item.type}>
        {item.list.map((entry, i) => <li key={i}>{renderInline(entry)}</li>)}
      </ListTag>
    );
  }
  if (item.note) {
    return <div className={styles.note}><p>{renderInline(item.note)}</p></div>;
  }
  if (item.n) {
    return (
      <div className={styles.point}>
        <span>{item.n}</span>
        <p>{renderInline(item.text)}</p>
      </div>
    );
  }
  return <p>{renderInline(item.text)}</p>;
};

const DocBody = ({ doc }) => (
  <>
    {doc.intro && <p className={styles.intro}>{renderInline(doc.intro)}</p>}
    {(doc.preamble || []).map((item, i) => <Item key={i} item={item} />)}
    {(doc.sections || []).map((section, i) => (
      <React.Fragment key={i}>
        {section.sub ? <h3>{renderInline(section.heading)}</h3> : <h2>{renderInline(section.heading)}</h2>}
        {section.items.map((item, j) => <Item key={j} item={item} />)}
      </React.Fragment>
    ))}
  </>
);

const LegalDocument = ({ doc, className }) => (
  <div className={cn('row', className)}>
    <div className={`col-12 ${styles.content}`}>
      <DocBody doc={doc} />
      {doc.annex && (
        <div className={styles.annex}>
          <h1>{doc.annex.title}</h1>
          {doc.annex.meta && <p className={styles.meta}>{doc.annex.meta}</p>}
          <DocBody doc={doc.annex} />
        </div>
      )}
    </div>
  </div>
);

export default LegalDocument;
